import React, { useMemo, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import useEntries, { assetUrl, toExcerpt } from '../Common/useEntries';
import ContentCard from '../Common/ContentCard';
import { directorateName, liveSubdomainForDirectorate } from '../../subdomains/portal';
import useSubdomainUrl from '../../subdomains/useSubdomainUrl';
import {
    Section,
    SectionHeading,
    LoadingState,
    EmptyState,
    FilterBar,
    FilterLabel,
} from '../Common/PageLayout';

/**
 * Grid of the Ministry's directorates and national directorates.
 *
 * `embedded` drops the surrounding section heading so the grid can be dropped
 * into another page (the Ministry profile) without repeating its title.
 */
export default function DirectorateList({ embedded = false }) {
    const siteUrl = useSubdomainUrl();
    const { items, loading } = useEntries({
        content_type: 'directorates',
        order: 'fields.directorateName',
        limit: 200,
    });
    const [term, setTerm] = useState('');

    const filtered = useMemo(() => {
        const needle = term.trim().toLowerCase();
        if (!needle) return items;
        return items.filter((entry) =>
            `${directorateName(entry.fields)} ${entry.fields?.description || ''}`
                .toLowerCase()
                .includes(needle)
        );
    }, [items, term]);

    const grid = (
        <>
            <FilterBar resultCount={loading ? null : filtered.length} resultNoun="directorates">
                <div>
                    <FilterLabel>Search directorates</FilterLabel>
                    <TextField
                        id="directorate-search"
                        variant="outlined"
                        size="small"
                        placeholder="Name or responsibility"
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="disabled" />
                                </InputAdornment>
                            ),
                        }}
                        style={{ minWidth: 280 }}
                    />
                </div>
            </FilterBar>

            {loading && <LoadingState label="Loading directorates" />}

            {!loading && filtered.length === 0 && (
                <EmptyState
                    title="No directorates match your search"
                    description="Try a different term, or clear the search box to see every directorate."
                />
            )}

            {!loading && filtered.length > 0 && (
                <Grid container spacing={3}>
                    {filtered.map((entry) => {
                        // Directorates with their own website (e.g. budget.mof.gov.tl) link straight to it.
                        const subdomain = liveSubdomainForDirectorate(entry);
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={entry.sys.id}>
                                <ContentCard
                                    title={directorateName(entry.fields)}
                                    description={toExcerpt(entry.fields?.description, 110)}
                                    image={assetUrl(entry.fields?.logodirectorate)}
                                    contain
                                    {...(subdomain
                                        ? { href: siteUrl(subdomain), sameTab: true, cta: `Visit ${subdomain}.mof.gov.tl` }
                                        : { to: `/directoratedetails/${entry.fields?.slug}`, cta: 'View directorate' })}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </>
    );

    if (embedded) return grid;

    return (
        <Section>
            <SectionHeading
                overline="Organisation"
                title="Directorates of the Ministry"
                description="Each directorate is responsible for a distinct part of public financial management, from budget preparation to treasury operations and state asset management."
            />
            {grid}
        </Section>
    );
}
